import _ from 'lodash';
import { get as getProperties } from './propertiesLogic';
import { fillTemplate } from '../templates/JsonTemplates';
import fixedOneTimeDiscount from '../templates/ChargesV2.discount.fixed-one-time.json';
function createLoyaltyDiscountOperator(items, value) {
    var operator = _.cloneDeep(fillTemplate({
        template: fixedOneTimeDiscount,
        params: { applyTo: items, amount: value },
    })).operator;
    return operator;
}
function extractItemIdsFromRawLoyaltyDiscount(charge) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (charge.type === 'discount' &&
        charge.operator.type === 'max' &&
        ((_b = (_a = charge.operator) === null || _a === void 0 ? void 0 : _a.operators) === null || _b === void 0 ? void 0 : _b[0].type) === 'multiply' &&
        ((_e = (_d = (_c = charge.operator) === null || _c === void 0 ? void 0 : _c.operators) === null || _d === void 0 ? void 0 : _d[0].numerators) === null || _e === void 0 ? void 0 : _e[0].type) === 'sum_prices') {
        return _.cloneDeep((_h = (_g = (_f = charge.operator) === null || _f === void 0 ? void 0 : _f.operators) === null || _g === void 0 ? void 0 : _g[0].numerators) === null || _h === void 0 ? void 0 : _h[0].items);
    }
    else {
        throw new Error('Not supported!');
    }
}
export function isLoyaltyDiscount(chargeV2) {
    return Boolean(getProperties(chargeV2, 'isLoyaltyDiscount'));
}
export function applyOperatorToLoyaltyDiscounts(chargesV2, redeemAmount) {
    var result = chargesV2;
    if (redeemAmount) {
        result = _.cloneDeep(chargesV2);
        var loyaltyDiscounts = result.filter(function (chargeV2) {
            return isLoyaltyDiscount(chargeV2) && chargeV2.state === 'operational';
        });
        loyaltyDiscounts.forEach(function (loyaltyDiscount) {
            var idsFilter = extractItemIdsFromRawLoyaltyDiscount(loyaltyDiscount);
            loyaltyDiscount.operator = createLoyaltyDiscountOperator(idsFilter, redeemAmount);
        });
    }
    return result;
}
export function getMaxRedeemablePoints(_a) {
    var pointsRequired = _a.pointsRequired, pointsBalance = _a.pointsBalance, subtotal = _a.subtotal, _b = _a.currencyValue, currencyValue = _b === void 0 ? 1 : _b;
    var maxRedeemablePoints = 0;
    while (maxRedeemablePoints <= pointsBalance &&
        (maxRedeemablePoints / pointsRequired) * currencyValue * 100 < subtotal) {
        maxRedeemablePoints += pointsRequired;
    }
    maxRedeemablePoints = Math.max(maxRedeemablePoints - pointsRequired, 0);
    return {
        maxRedeemablePoints: maxRedeemablePoints,
        maxRedeemableCents: (maxRedeemablePoints / pointsRequired) * 100 * currencyValue,
    };
}
export function convertLoyaltyPointsToCents(_a) {
    var _b = _a.pointsAmount, pointsAmount = _b === void 0 ? 0 : _b, pointsRequired = _a.pointsRequired, currencyValue = _a.currencyValue;
    return (pointsAmount / pointsRequired) * 100 * currencyValue;
}
export function convertCentsToLoyaltyPoints(_a) {
    var _b = _a.centsAmount, centsAmount = _b === void 0 ? 0 : _b, pointsRequired = _a.pointsRequired, currencyValue = _a.currencyValue;
    if (currencyValue === 0) {
        // verify we're not dividing by zero
        return 0;
    }
    else {
        return Math.floor((centsAmount * pointsRequired) / currencyValue / 100);
    }
}
export function hasLoyaltySupport(chargesV2) {
    if (chargesV2 === void 0) { chargesV2 = []; }
    return Boolean(chargesV2.find(function (c) { return isLoyaltyDiscount(c); }));
}
function calcLoyaltyDiscountMoneyAmount(orderCharges, loyaltyChargesV2) {
    var sum = orderCharges
        .filter(function (oc) {
        var chargeV2 = loyaltyChargesV2.find(function (c) { return c.id === oc.chargeId; });
        return chargeV2 && isLoyaltyDiscount(chargeV2);
    })
        .reduce(function (acc, oc) { return acc + oc.amount; }, 0);
    // avoid returning "-0"
    return sum === 0 ? 0 : sum * -1;
}
export function createOrderLoyaltyObject(_a) {
    var orderCharges = _a.orderCharges, chargesV2 = _a.chargesV2, subtotal = _a.subtotal, pointsRequired = _a.pointsRequired, currencyValue = _a.currencyValue, pointsBalance = _a.pointsBalance, loyaltyRewardId = _a.loyaltyRewardId, loyaltyRewardRevision = _a.loyaltyRewardRevision, earnPointsRequired = _a.earnPointsRequired, earnCurrencyValue = _a.earnCurrencyValue, earningRules = _a.earningRules;
    var moneyAmount = calcLoyaltyDiscountMoneyAmount(orderCharges, chargesV2);
    var estimatedPointsEarned = earningRules
        ? calcEarnedPoints({ rules: earningRules, moneyAmountInCents: subtotal })
        : convertCentsToLoyaltyPoints({
            centsAmount: subtotal,
            pointsRequired: earnPointsRequired || 1,
            currencyValue: earnCurrencyValue || 1,
        });
    var estimatedPointsRedeemed = convertCentsToLoyaltyPoints({
        centsAmount: moneyAmount,
        pointsRequired: pointsRequired,
        currencyValue: currencyValue,
    });
    var estimatedAccountBalance = pointsBalance - estimatedPointsRedeemed + estimatedPointsEarned;
    return {
        rewardId: loyaltyRewardId,
        rewardRevision: loyaltyRewardRevision.toString(),
        moneyAmount: moneyAmount,
        estimatedPointsEarned: estimatedPointsEarned,
        estimatedAccountBalance: estimatedAccountBalance,
    };
}
export function calcEarnedPoints(_a) {
    var rules = _a.rules, moneyAmountInCents = _a.moneyAmountInCents;
    var points = 0;
    rules.forEach(function (earningRule) {
        var _a, _b;
        points += calcSumOfPoints(moneyAmountInCents, (_a = earningRule.conversionRule) === null || _a === void 0 ? void 0 : _a.moneyAmount, (_b = earningRule.conversionRule) === null || _b === void 0 ? void 0 : _b.points);
    });
    return points;
}
function calcSumOfPoints(moneyAmountInCents, moneyAmount, pointAmount) {
    if (moneyAmount === void 0) { moneyAmount = 0; }
    if (pointAmount === void 0) { pointAmount = 0; }
    return Math.floor(moneyAmountInCents / 100 / moneyAmount) * pointAmount;
}
//# sourceMappingURL=redeemChargeLogic.js.map